/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  hasWSError,
  // HeartbeatRequest,
  Message,
  ActivesMessage,
  PostRequest,
  WSFullRequest,
  WSRequest,
  WSResponse,
} from '@cloudio-saas/datasource-types';
import { getURLQueryStringToPost, reloadPage } from 'utils/url';
import {
  emptyFunction,
  useStateXAction,
  useStateXGetter,
  useStateXQuietSetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { SendRequest } from 'componentregistry';
import useMounted from 'hooks/useMounted';
import AbortError from 'platform/AbortError';
import { PlatformContext } from 'platform/PlatformProvider';
import { processResponseAction } from 'platform/RecentSQLState';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { userAtom } from 'root/useUser';
import DSAttributesCache from 'store/DSAttributesCache';
import { getStore } from 'store/useDataStore';
import {
  getDataSourceListStoreProps,
  getIoDatasourceStoreProps,
  getIoLookupsStoreProps,
  getIoLookupValuesStoreProps,
} from 'store/utils/utils';
import { globalCache } from 'utils/cache';
import Logger from 'utils/Logger';
import { ActivePubSub } from 'utils/Active';
import useNavigator from 'routes/useNavigator';
import { getWSUrl } from '../config/config';
import { readyStateAtom } from './OnlineIndicator';
import useConstant from 'hooks/useConstant';
import assert from 'utils/assert';
import { clearDBTypes } from 'layout/Sidebar/panels/DataSourceView/CodeEditorTypes';
import { isValidSession } from '../routes/SecureRoute';
import {
  liveStatusSignal,
  scheduleUpdateSignal,
} from '../layout/Sidebar/atoms';
import IOError from '../platform/IOError';
import { gunzip, gzip } from 'web_worker';
import { lastMessageAtom } from '../layout/Sidebar/panels/TestsView/state';
import { refreshPageMenuAtom } from 'layout/Header/PageMenuIcon';
import { disableMessageCompressionAtom } from 'designer/atoms';
import { sessionTimeoutDialogAction } from 'custom/SessionTimeoutDialog';

interface Pending {
  currentRequestId: 0;
  currentPostRequestId: 0;
  currentSystemRequestId: 0;
  pending: {
    [key: string]: [
      WSFullRequest<WSRequest>,
      (value: WSResponse | PromiseLike<WSResponse>) => void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (reason?: any) => void,
      boolean,
    ];
  };
  pendingBatch: {
    [key: string]: [
      string[],
      (value: boolean | PromiseLike<boolean>) => void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (reason?: any) => void,
    ];
  };
  currentBatch: string[];
  batchMode: boolean;
  messageQueue: WSFullRequest<WSRequest>[];
  batchFunctions: ((
    batchCallback: () => void,
    cancelBatch: () => void,
  ) => void)[];
}

type Handler = (response: ActivesMessage) => void;

export interface WebSocketProps {
  addListener: (handler: Handler) => () => void;
  addReconnectListener: (handler: ReconnectHandler) => () => void;
  close: () => void;
  doConnect: () => void;
  hashRef: { current: string };
  isSocketOpen: () => boolean;
  readyState: () => number;
  sendRequest: SendRequest;
  startBatch: () => void;
  executeBatch: () => Promise<boolean>;
  addToBatch: (
    fn: (batchCallback: () => void, cancelBatch: () => void) => void,
  ) => void;
  isIdle: () => boolean;
}

const handlers = new Set<Handler>();

function addListener(handler: Handler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

type ReconnectHandler = () => void;

const reconnectHandlers = new Set<ReconnectHandler>();

function addReconnectListener(handler: ReconnectHandler): () => void {
  reconnectHandlers.add(handler);
  return () => reconnectHandlers.delete(handler);
}

const EMPTY_FUNCTION = () => {};
let latestId = 0;

export const WebSocketContext = React.createContext<WebSocketProps>({
  addListener,
  addReconnectListener,
  close: EMPTY_FUNCTION,
  doConnect: EMPTY_FUNCTION,
  hashRef: { current: '#/' },
  isSocketOpen: () => false,
  readyState: () => -1,
  sendRequest: () => [
    Promise.resolve({
      action: 'Action',
      status: 'success',
      uid: '',
      timestamp: -1,
      data: {},
    }),
    'INITIAL_ID',
  ],
  startBatch: EMPTY_FUNCTION,
  executeBatch: async () => false,
  addToBatch: EMPTY_FUNCTION,
  isIdle: () => true,
});

let active = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isMessage(m: any): m is Message {
  return typeof m.type === 'string';
}

export const useWebSocket = (): WebSocketProps => {
  const socketRef = useRef<WebSocket | null>(null);
  const isOpen = useRef(false);
  const reconnect = useRef(false);
  const hashRef = useRef('#/');
  const isMounted = useMounted();
  const set = useStateXSetter();
  const get = useStateXGetter();
  const session = get(userAtom);
  const { userName = 'GUEST' } = session || {};
  const id = useConstant(() => ++latestId);

  if (!session) {
    throw Error('Missing session!');
  }
  const platform = useContext(PlatformContext);
  const dsCache = useMemo(
    () =>
      new DSAttributesCache({
        set,
        get,
        platform,
      }),
    [get, platform, set],
  );
  const retryCountRef = useRef(0);
  const reconnectRef = useRef({
    totalCount: 0,
    batchCount: 0,
    batchStart: 0,
  });

  const navigator = useNavigator();

  const requests = useRef<Pending>({
    currentRequestId: 0,
    currentPostRequestId: 0,
    currentSystemRequestId: 0,
    pending: {},
    messageQueue: [],
    pendingBatch: {},
    batchMode: false,
    currentBatch: [],
    batchFunctions: [],
  });

  const sendMessage = useCallback(
    async (message: WSFullRequest<WSRequest>) => {
      if (platform.sessionTimeout) {
        console.warn(
          'WebSocket Session timed out. Skipping sendMessage. messageType = ',
          message?.type,
        );
        return;
      }
      if (id !== latestId) {
        throw Error('Calling sendMessage on a stale websocket!');
      }
      if (socketRef.current != null) {
        try {
          if (socketRef.current.readyState !== WebSocket.OPEN) {
            requests.current.messageQueue.push(message);
          } else if (socketRef.current.readyState === WebSocket.OPEN) {
            const disableCompression = platform.getter(
              disableMessageCompressionAtom,
              { params: { userName } },
            );
            if (disableCompression) {
              message.noGzip = true;
            }
            const messageString = JSON.stringify(message);
            const compressedMessage =
              !disableCompression && messageString.length > 2000
                ? await gzip(messageString)
                : messageString;
            socketRef.current.send(compressedMessage);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        requests.current.messageQueue.push(message);
      }
    },
    [id, platform, userName],
  );

  const isSocketOpen = useCallback(() => isOpen.current, []);

  const readyState = useCallback(() => {
    if (socketRef.current) {
      return socketRef.current.readyState;
    }
    return -1;
  }, []);

  const startBatch = useCallback((): void => {
    if (requests.current.batchMode) {
      throw Error('Batch already started!');
    }
    requests.current.currentBatch = [];
    requests.current.batchMode = true;
  }, []);

  const _executeBatch = useCallback(
    (
      resolve: (value: boolean | PromiseLike<boolean>) => void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      reject: (reason?: any) => void,
    ) => {
      if (requests.current.currentBatch.length === 0) {
        requests.current.batchMode = false;
        resolve(false);
        return;
      }

      const batchUid = `b${requests.current.currentRequestId++}`;
      const combinedRequest = requests.current.currentBatch.reduce(
        (_combinedRequest, uid, index) => {
          const [req] = requests.current.pending[uid];
          if (req.type === 'Post') {
            _combinedRequest.data = {
              ..._combinedRequest.data,
              ...req.data,
            };
          }
          if (!_combinedRequest.appUid && req.appUid) {
            _combinedRequest.appUid = req.appUid;
          }
          if (!_combinedRequest.roleUid && req.roleUid) {
            _combinedRequest.roleUid = req.roleUid;
          }
          if (!_combinedRequest.pageUid && req.pageUid) {
            _combinedRequest.pageUid = req.pageUid;
          }
          return _combinedRequest;
        },
        {
          type: 'Post',
          appUid: '',
          timestamp: new Date().getTime(),
          sessionId: session.sessionId,
          data: {},
          uid: batchUid,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as WSFullRequest<PostRequest<any>>,
      );
      const currentBatch = requests.current.currentBatch;
      requests.current.pendingBatch[batchUid] = [currentBatch, resolve, reject];
      sendMessage(combinedRequest);
      requests.current.currentBatch = [];
      requests.current.batchMode = false;
    },
    [sendMessage, session.sessionId],
  );

  const executeBatch = useCallback((): Promise<boolean> => {
    const q = requests.current.batchFunctions;
    requests.current.batchFunctions = [];
    function cancelBatch() {
      requests.current.currentBatch = [];
      requests.current.batchMode = false;
    }
    return new Promise<boolean>((resolve, reject) => {
      let count = q.length;
      const cb = () => {
        count--;
        if (count === 0) {
          _executeBatch(resolve, reject);
        }
      };
      for (let i = 0; i < q.length; i++) {
        q[i](cb, cancelBatch);
      }
    });
  }, [_executeBatch]);

  const addToBatch = useCallback(
    (
      fn: (batchCallback: () => void, cancelBatch: () => void) => void,
    ): void => {
      requests.current.batchFunctions.push(fn);
    },
    [],
  );

  const isIdle = useCallback((): boolean => {
    return (
      Object.keys(requests.current.pending).length === 0 &&
      Object.keys(requests.current.pendingBatch).length === 0
    );
  }, []);

  const processResponsesql = useStateXAction(processResponseAction);
  const setQuietly = useStateXQuietSetter();

  const sendRequest = useCallback(
    (
      req: WSRequest,
      {
        appUid,
        test = false,
        pageUid,
        roleUid,
      }: { appUid: string; roleUid?: string; pageUid?: string; test?: boolean },
    ): [Promise<WSResponse>, string] => {
      if (id !== latestId) {
        throw Error('Calling sendRequest on a stale websocket!');
      }
      if (req.type === 'Post' || req.type === 'PostAtom') {
        ActivePubSub.pub('Activity', { type: 'Request' });
      }
      const uid =
        req.type === 'Post'
          ? req.system
            ? `s${requests.current.currentRequestId - 1}-${requests.current
                .currentSystemRequestId++}`
            : `r${requests.current.currentPostRequestId++}`
          : `rs${requests.current.currentRequestId++}`;

      assert(req.type !== 'Exit' || !!req._id, 'Missing Exit._id!');
      const request: WSFullRequest<WSRequest> = {
        ...req,
        uid,
        appUid,
        roleUid,
        pageUid,
        timestamp: new Date().getTime(),
        sessionId: session.sessionId,
      };
      if (req.type === 'FetchAtom') {
        request.appUid = req.appUid;
      }
      const p: Promise<WSResponse> = new Promise<WSResponse>(
        (resolve, reject) => {
          requests.current.pending[uid] = [request, resolve, reject, test];
        },
      );
      if (
        requests.current.batchMode &&
        request.type === 'Post' &&
        !request.skipBatchMode
      ) {
        // console.log('pushing to batch ', uid);
        requests.current.currentBatch.push(uid);
      } else {
        if (request.type === 'Post' && request.skipBatchMode) {
          delete request.skipBatchMode;
        }
        sendMessage(request);
        setQuietly(lastMessageAtom, { req, appUid });
      }
      return [p, uid];
    },
    [id, sendMessage, session.sessionId, setQuietly],
  );

  const listener = useCallback(
    async (e: MessageEvent) => {
      if (isMounted.current && !platform.sessionTimeout) {
        let json = '';
        if (typeof e.data === 'string') {
          json = e.data;
        } else {
          const array = new Uint8Array(await (e.data as Blob).arrayBuffer());
          json = await gunzip(array);
        }
        const response: WSResponse | Message = JSON.parse(json);
        if (isMessage(response)) {
          switch (response.type) {
            case 'Alert':
              platform.showMessage({
                msg: response.message,
                type: response.alertType,
              });
              break;
            case 'SignOut':
              if (!globalCache.signingOut) {
                globalCache.clearAll();
                navigator.push('/signin');
                reloadPage();
              }
              break;
            case 'RefreshDataSource':
              clearDBTypes();
              if (response.refreshType === 'Attributes') {
                if (Logger.isLogEnabled()) {
                  Logger.log({
                    itemType: 'WS',
                    status: 'debug',
                    msg: `Refresh Attributes [${response.appUid}.${response.ds}]`,
                  });
                }
                dsCache.refreshAttributes(
                  response.appUid,
                  response.ds,
                  undefined,
                );
                const dependentAppUids = DSAttributesCache.getDependentAppUids(
                  response.appUid,
                  response.ds,
                );
                for (const appUid of dependentAppUids) {
                  dsCache.refreshAttributes(
                    appUid,
                    response.ds,
                    response.appUid,
                  );
                }
              } else {
                const store = getStore(
                  getDataSourceListStoreProps(response.appUid),
                );
                if (store) {
                  if (Logger.isLogEnabled()) {
                    Logger.log({
                      itemType: 'WS',
                      status: 'debug',
                      msg: `Refresh Store [${store.appUid}.${store.ds}.${store.alias}]`,
                    });
                  }
                  store.refresh(true);
                }
                const store2 = getStore(
                  getIoDatasourceStoreProps(response.appUid),
                );
                if (store2 && !store2.isStoreDirty()) {
                  if (Logger.isLogEnabled()) {
                    Logger.log({
                      itemType: 'WS',
                      status: 'debug',
                      msg: `Refresh Store [${store2.appUid}.${store2.ds}.${store2.alias}]`,
                    });
                  }
                  // do not refresh if in the middle of creating a datasource
                  const _id = store2.getCurrentRecordId();
                  store2.refresh(true).finally(() => {
                    store2.setCurrentRecordId(_id);
                  });
                } else if (store2) {
                  if (Logger.isLogEnabled()) {
                    Logger.log({
                      itemType: 'WS',
                      status: 'debug',
                      msg: `Skipping Refresh Store as the store is dirty [${store2.appUid}.${store2.ds}.${store2.alias}]`,
                    });
                  }
                }
              }
              break;
            case 'RefreshLoookup': {
              if (response.ds === 'IoLookupValues') {
                const store = getStore(
                  getIoLookupValuesStoreProps(
                    response.appUid,
                    response.lookupUid,
                  ),
                );
                if (store) {
                  if (Logger.isLogEnabled()) {
                    Logger.log({
                      itemType: 'WS',
                      status: 'debug',
                      msg: `Refresh Lookup Values Store [${store.appUid}.${store.ds}.${store.alias}]`,
                    });
                  }
                  platform.startAsyncTransition(async () => {
                    store.refresh(true);
                  }, emptyFunction);
                }
              } else {
                const store = getStore(getIoLookupsStoreProps(response.appUid));
                if (store) {
                  platform.startAsyncTransition(async () => {
                    store.refresh(true);
                  }, emptyFunction);
                }
              }
              break;
            }
            case 'RefreshNavigationMenu': {
              // no need to refresh the store right away
              // defer until the user click on the page navigation icon
              // this is to prevent rerendering the app and there by
              // avoid resetting the selection state on page tree in dev mode
              platform.setter(refreshPageMenuAtom, true);
              break;
            }
            case 'LiveStatus': {
              liveStatusSignal[response.liveStatus.uid].set(
                response.liveStatus,
              );
              break;
            }
            case 'ScheduleUpdate': {
              scheduleUpdateSignal[response.scheduleUpdate.jobUid].set(
                response.scheduleUpdate,
              );
              break;
            }
            default:
              handlers.forEach((handler) => handler(response));
          }
          return;
        }

        const { uid } = response;
        if (hasWSError(response)) {
          const { status, message } = response;
          if (message) {
            if (message === 'SESSION_TIMEDOUT') {
              if (!globalCache.signingOut) {
                platform.sessionTimeout = true;
                const confirmed = await new Promise((resolve) => {
                  platform.caller(sessionTimeoutDialogAction, {
                    onOk: () => {
                      resolve(true);
                    },
                  });
                });
                if (confirmed) {
                  navigator.push('/signin');
                  reloadPage();
                }
              }
            } else if (message === 'INVALID_SESSION') {
              if (!globalCache.signingOut) {
                globalCache.clearAll();
                navigator.push('/signin');
                reloadPage();
              }
            } else if (
              uid &&
              ((requests.current.pending[uid] &&
                !requests.current.pending[uid][3]) ||
                requests.current.pendingBatch[uid]) // need to check for batch as well
            ) {
              platform.showMessage({ msg: message, type: status });
            }
          }
        }

        if (uid && requests.current.pendingBatch[uid]) {
          // batch response
          const [batch, batchResolve, batchReject] =
            requests.current.pendingBatch[uid];
          delete requests.current.pendingBatch[uid];
          if (response.status === 'success') {
            processResponsesql(response);
          }
          batch.forEach((_uid, index) => {
            const [, resolve, reject] = requests.current.pending[_uid];
            delete requests.current.pending[_uid];
            if (response.status === 'success') {
              resolve({ ...response, uid: _uid });
            } else {
              reject(new AbortError());
            }
          });
          if (response.status === 'success') {
            batchResolve(true);
          } else {
            batchReject(new AbortError());
          }
        } else if (uid && requests.current.pending[uid]) {
          const [req, resolve, reject, test] = requests.current.pending[uid];
          delete requests.current.pending[uid];
          if (response.status === 'success') {
            processResponsesql(response);
            resolve(response);
          } else {
            if (Logger.isLogEnabled()) {
              Logger.log({
                itemType: 'WS',
                status: 'error',
                msg: JSON.stringify(req),
              });
            }
            test
              ? reject(new IOError(response.title, response.message))
              : reject(new AbortError());
          }
        }
      }
    },
    [isMounted, platform, navigator, dsCache, processResponsesql],
  );

  function callReconnectListeners() {
    reconnectHandlers.forEach((handler) => handler());
  }

  const connect = useCallback(() => {
    if (!isMounted.current) {
      return;
    }
    if (socketRef.current?.readyState) {
      console.warn('aleady connected!', socketRef.current?.readyState);
      return;
    }
    if (active) {
      console.warn('in active connect...');
      return;
    }
    active = true;
    let webSocket: WebSocket;
    try {
      const isImpersonating =
        session.sessionId?.startsWith('imp_') && session.impersonateUserAccess;
      const impersonationFlag = isImpersonating ? '&x_imp=1' : '';
      const wsUrl = `${getWSUrl()}?${getURLQueryStringToPost(
        session,
      )}${impersonationFlag}`;
      webSocket = new WebSocket(wsUrl);

      set(readyStateAtom, webSocket.readyState ?? -1);
      socketRef.current = webSocket;
      webSocket.onopen = () => {
        // console.log('onopen', { id, latestId });
        if (id !== latestId) {
          throw Error('Calling onopen on a stale websocket!');
        }
        if (socketRef.current) {
          retryCountRef.current = 0;
          set(readyStateAtom, socketRef.current.readyState);
          isOpen.current = true;
          const queue = requests.current.messageQueue;
          requests.current.messageQueue = [];
          queue.forEach((m) => {
            sendMessage(m);
          });
          if (isMounted.current && reconnect.current) {
            if (Logger.isLogEnabled()) {
              Logger.log({
                itemType: 'WS',
                status: 'debug',
                msg: 'Reconnected...',
              });
            }
            callReconnectListeners();
          }
          reconnect.current = true;
        }
      };
      webSocket.onclose = async (e) => {
        console.error(e);
        if (id !== latestId) {
          throw Error('Calling onclose on a stale websocket!');
        }
        active = false;
        isOpen.current = false;
        if (isMounted.current) {
          reconnectRef.current.totalCount += 1;
          reconnectRef.current.batchCount += 1;
          const ms = new Date().getTime();
          if (ms - reconnectRef.current.batchStart < 10000) {
            // closing within 10 seconds
            if (reconnectRef.current.batchCount > 10) {
              // closed 10 times within the last 10 seconds
              console.error(
                e.code,
                e.reason,
                retryCountRef.current,
                reconnectRef.current,
              );
              set(readyStateAtom, -9);
              if (socketRef.current) {
                const ws = socketRef.current;
                socketRef.current = null;
                ws.close();
              }
              return;
            }
          } else {
            reconnectRef.current.batchCount = 0;
            reconnectRef.current.batchStart = ms;
          }
          // check if not server restarting
          if (e.code !== 1012) {
            if (socketRef.current) {
              set(readyStateAtom, socketRef.current.readyState);
            } else {
              set(readyStateAtom, WebSocket.CLOSED);
            }
          }
          if (socketRef.current) {
            const ws = socketRef.current;
            socketRef.current = null;
            ws.close();
          }
          if (
            e.reason === 'INVALID_SESSION' ||
            e.reason === 'MISSING_SESSIONID'
          ) {
            // signOut(); TODO
          }
          if (e.code === 1012) {
            // server restarting
            set(readyStateAtom, -9);
          } else if (
            (e.code === 1005 || e.code === 1006) &&
            retryCountRef.current < 10
          ) {
            retryCountRef.current += 1;
            connect();
          } else {
            set(readyStateAtom, -9);
            const valid = await isValidSession(session);
            if (valid === 'ERROR') {
              if (!globalCache.signingOut) {
                globalCache.clearAll();
                navigator.push('/signin');
                reloadPage();
              }
              return;
            } else if (valid === 'DOWN') {
              platform.showError('Server or the network may be down!');
              return;
            }
            console.error(e.code, e.reason, retryCountRef.current);
            if (Logger.isLogEnabled()) {
              Logger.log({
                itemType: 'WS',
                status: 'debug',
                msg: `Connection closed by server. Code: ${e.code} Reason: ${e.reason}`,
              });
            }
            platform.showError(
              `Connection closed by server. Code: ${e.code}${
                e.reason ? `, Reason: ${e.reason}` : ''
              }.`,
            );
          }
        }
      };
      webSocket.onerror = (e) => {
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: 'WS',
            status: 'debug',
            msg: `On Error: ${e.type}`,
          });
        }
      };
      webSocket.addEventListener('message', listener);
    } catch (error) {
      active = false;
      console.error('ws error');
      console.error(error);
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: 'WS',
          status: 'debug',
          msg: `Error: ${
            error instanceof Error ? error.message : String(error)
          }`,
        });
      }
    }
  }, [id, isMounted, listener, navigator, platform, sendMessage, session, set]);

  const doConnect = useCallback(() => {
    connect();
  }, [connect]);

  const close = useCallback(() => {
    active = false;
    set(readyStateAtom, -1);
    if (socketRef.current) {
      const ws = socketRef.current;
      socketRef.current = null;
      ws.close();
    }
  }, [set]);

  useEffect(() => {
    doConnect();
    return () => {
      close();
    };
  }, [doConnect, close, id]);

  const value = useMemo(
    () => ({
      addListener,
      addReconnectListener,
      close,
      doConnect,
      hashRef,
      isSocketOpen,
      readyState,
      sendRequest,
      startBatch,
      executeBatch,
      addToBatch,
      isIdle,
    }),
    [
      addToBatch,
      close,
      doConnect,
      executeBatch,
      isIdle,
      isSocketOpen,
      readyState,
      sendRequest,
      startBatch,
    ],
  );

  return value;
};

export function useWS() {
  return useContext(WebSocketContext);
}
